import { Button, Dropdown } from "@/atoms"
import { getTicketingSystemFromUrl } from "@/domain/ticketing"
import { getUnavailableVendorMessage } from "@/helpers/error.helper"
import { buildLinkTicketRequest } from "@/helpers/ticketing.helpers"
import { preventDefault } from "@/helpers/utils"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { DisconnectOutlined, MoreOutlined } from "@/icons"
import { Protected } from "@/molecules/Protected"
import { useUnlinkTicketFromIssue } from "@/organisms/Drawers/applications/triage/useTriage"

import styles from "./Action.module.scss"

import type { AsideActionCardProps } from "."

export const AsideActionCard = (props: AsideActionCardProps) => {
  const { ticket, issueId } = props
  const { name: issueKey, url } = ticket
  const { isUnlinking, unlink, message } = useUnlinkTicketFromIssue()
  const { t } = useTranslation()
  const { logger, EVENTS } = useLogger()

  return (
    <Dropdown
      menu={{
        items: [
          {
            key: "remove",
            className: styles.dropdownActions,
            label: (
              <Protected
                permission={{ resource: "tickets", action: "write" }}
                shouldDisable
              >
                <Button
                  loading={isUnlinking}
                  icon={<DisconnectOutlined />}
                  type="text"
                >
                  {t("general.remove")}
                </Button>
              </Protected>
            )
          }
        ],
        onClick: (info) => {
          const { key, domEvent } = info

          if (key === "remove") {
            preventDefault(domEvent)

            message.loading({
              duration: 0,
              content: t("jiraIssueLink.removingLink"),
              key: issueKey
            })

            unlink(
              buildLinkTicketRequest(
                getTicketingSystemFromUrl(url),
                issueId,
                issueKey
              )
            )
              .then((response) => {
                if (!response?.ok) throw Error("Error unlinking Jira issue")

                message.success({
                  content: t("jiraIssueLink.removeLinkSuccess"),
                  key: issueKey,
                  duration: 3
                })
              })
              .catch((error) => {
                message.error({
                  content:
                    getUnavailableVendorMessage(error) ??
                    t("jiraIssueLink.removeLinkError"),
                  key: issueKey,
                  duration: 3
                })

                logger.error(error, {
                  issueId,
                  issueKey,
                  custom_event:
                    EVENTS.ERROR_EVENTS.JIRA.UNLINK_JIRA_TO_ISSUE_FAILURE
                })
              })
          }
        }
      }}
      placement="bottomRight"
    >
      <Button
        icon={<MoreOutlined />}
        type="text"
        size="small"
        onClick={preventDefault}
      />
    </Dropdown>
  )
}
